/**
 * Andar Bahar — the rules, pure. Plan §6.2 (docs/ADRENALINE_SHARED_TABLES_PLAN.md).
 *
 * A joker is drawn at round OPEN; players bet ANDAR or BAHAR; dealing
 * alternates starting on ANDAR if the joker is black, BAHAR if red, until a
 * card matches the joker's RANK. The side that received the FIRST card pays
 * X1.90, the other X2.00 (the first side wins 51.5% of the time — that
 * asymmetry IS the house edge: 2.15% / 3.00%).
 *
 * The natural prior is over k = the position of the matching card (1..49);
 * the SIDE is determined by k (odd → first side). The server's scorer will
 * pick k from this prior; concrete cards are then sampled to fit k.
 *
 * X-DOCTRINE: every multiplier shown is TOTAL RETURN at 2 dp.
 */
import { type Card, type Rank, freshDeck, shuffle, isRed, RANKS } from '../_cards/deck';

export type Side = 'andar' | 'bahar';

/** the side that gets the first card: black joker → ANDAR, red → BAHAR */
export const firstSideFor = (joker: Card): Side => (isRed(joker) ? 'bahar' : 'andar');
export const otherSide = (s: Side): Side => (s === 'andar' ? 'bahar' : 'andar');

/** total-return payouts: the first side X1.90, the second X2.00 */
export const PAY_FIRST = 1.9;
export const PAY_SECOND = 2.0;
export function payoutFor(side: Side, joker: Card): number {
  return side === firstSideFor(joker) ? PAY_FIRST : PAY_SECOND;
}
/** what a winning bet returns: floor(stake × X) */
export const winReturn = (stake: number, x: number) => Math.floor(stake * x + 1e-9);

/** the side that wins when the match lands at position k (1-based) */
export const sideForK = (k: number, joker: Card): Side => (k % 2 === 1 ? firstSideFor(joker) : otherSide(firstSideFor(joker)));

/** natural prior P(k): 3 matching ranks among the 51 remaining cards.
 *  P(k) = [∏_{i=1}^{k−1} (49−i)/(52−i)] · 3/(52−k), k = 1..49 */
export function priorK(): number[] {
  const p: number[] = [0];
  let survive = 1;
  for (let k = 1; k <= 49; k++) {
    p[k] = survive * 3 / (52 - k);
    survive *= (49 - k) / (52 - k);
  }
  return p;
}
const PRIOR = priorK();
export const P_FIRST_SIDE = PRIOR.reduce((s, v, k) => s + (k % 2 === 1 ? v : 0), 0);

/** sample k from the prior. PHASE-2 SEAM: the server's scorer chooses k. */
export function sampleK(rng: () => number): number {
  let u = rng();
  for (let k = 1; k <= 49; k++) { u -= PRIOR[k]; if (u <= 0) return k; }
  return 49;
}

/** deal a concrete sequence that matches at position k: draw the joker's rank
 *  siblings and the non-matching cards from a shuffled deck so the k-th card
 *  is the first rank match. Returns the dealt cards in order (length k). */
export function dealFor(joker: Card, k: number, rng: () => number): Card[] {
  const deck = shuffle(freshDeck().filter((c) => !(c.rank === joker.rank && c.suit === joker.suit)), rng);
  const matches = deck.filter((c) => c.rank === joker.rank);
  const others = deck.filter((c) => c.rank !== joker.rank);
  const out: Card[] = [];
  for (let i = 1; i < k; i++) out.push(others[i - 1]);
  out.push(matches[0]);
  return out;
}

/** draw the round's joker from a fresh shuffled deck */
export function drawJoker(rng: () => number): Card {
  const d = shuffle(freshDeck(), rng);
  return d[0];
}

export const fmtX = (x: number) => `X${(Math.floor(x * 100 + 1e-9) / 100).toFixed(2)}`;
export const rankLabel = (r: Rank) => r;
export { RANKS };
